import React from "react";
import { TabContent, TabPane } from "reactstrap";
import CsvToTable from "src/components/Common/CsvToTable";
import ReactDiffViewer from "react-diff-viewer";
import { isCSV, safeStringify } from "src/utils/common";
import { SIMULATION_TYPE, EXECUTION_FILE_TYPE } from "src/constant/common";

const TabContentArea = ({
  activeTab,
  fileData,
  serieData,
  simulationType,
  executionFileName,
  isNewVersion,
  isHasExcelFiles,
  getTabId,
  getAeroContent,
  getExcelFilesContent,
  isCSVContent,
  showJsonContent,
  t,
}) => {
  const renderMainFileTab = () => (
    <TabPane tabId={getTabId("main_file")}>
      {isCSV(fileData?.name) ? (
        isCSVContent(fileData?.fileContent) ? (
          <CsvToTable csvData={fileData?.fileContent} />
        ) : (
          <div>{t("file_error")}</div>
        )
      ) : (
        showJsonContent(fileData?.fileContent || fileData?.jsonObject)
      )}
    </TabPane>
  );

  const renderSeriesResponseTab = () => {
    if (!serieData?.fileResponseLink) return null;
    return <TabPane tabId={getTabId("series_response")}>{showJsonContent(serieData?.fileResponse)}</TabPane>;
  };

  const render3DOFContent = () => {
    if (simulationType !== SIMULATION_TYPE["3DOF"]) return null;

    return (
      <>
        {/* Aero files content for new version */}
        {isNewVersion &&
          fileData?.aero_files &&
          Array.isArray(fileData.aero_files) &&
          fileData.aero_files
            .filter((aeroFile) => aeroFile?.aero_name && isCSV(aeroFile.aero_name))
            .map((aeroFile, index) => {
              const aeroFileContent =
                fileData?.aeroFilesContent?.find((contentFile) => contentFile.aero_name === aeroFile.aero_name)?.content || null;
              const tabId = getTabId("aero_file", index);
              return (
                <TabPane key={`aero-file-tab-${index}`} tabId={tabId} className="m-3">
                  {getAeroContent(aeroFile?.aero_name, aeroFile?.aero_link, aeroFileContent)}
                </TabPane>
              );
            })}

        {/* Aero files content for old version */}
        {!isNewVersion && fileData?.aero1_link && isCSV(fileData?.aero1_name) && (
          <TabPane tabId={getTabId("aero1")} className="m-3">
            {getAeroContent(fileData?.aero1_name, fileData?.aero1_link, fileData?.aero1Content)}
          </TabPane>
        )}
        {!isNewVersion && fileData?.aero2_link && isCSV(fileData?.aero2_name) && (
          <TabPane tabId={getTabId("aero2")} className="m-3">
            {getAeroContent(fileData?.aero2_name, fileData?.aero2_link, fileData?.aero2Content)}
          </TabPane>
        )}

        {/* Wind model content */}
        {fileData?.wind_model_link && (
          <TabPane tabId={getTabId("wind_model")} className="m-3">
            {isCSV(fileData?.wind_model_name) &&
              (isCSVContent(fileData?.windModelContent) ? (
                <CsvToTable csvData={fileData?.windModelContent} />
              ) : (
                <div>{t("file_error")}</div>
              ))}
          </TabPane>
        )}

        {/* Thrust profile content */}
        {fileData?.thrust_profile_link && (
          <TabPane tabId={getTabId("thrust_profile")} className="m-3">
            {isCSV(fileData.thrust_profile_name) &&
              (isCSVContent(fileData?.thrustProfileContent) ? (
                <CsvToTable csvData={fileData?.thrustProfileContent} />
              ) : (
                <div>{t("file_error")}</div>
              ))}
          </TabPane>
        )}

        {/* Excel files content */}
        {isHasExcelFiles(fileData) && (
          <TabPane tabId={getTabId("excel_files")} className="m-3">
            {getExcelFilesContent(fileData, simulationType, isNewVersion)}
          </TabPane>
        )}
      </>
    );
  };

  const render6DOFContent = () => {
    if (
      (simulationType !== SIMULATION_TYPE["6DOF"] && simulationType !== SIMULATION_TYPE["6DOF_MCS"]) ||
      (executionFileName !== EXECUTION_FILE_TYPE.MAIN_6DOF &&
        executionFileName !== EXECUTION_FILE_TYPE.MCS_6DOF &&
        executionFileName !== EXECUTION_FILE_TYPE.DISPERSION_6DOF &&
        executionFileName !== EXECUTION_FILE_TYPE.EXPECTED_CASUALTY_6DOF)
    ) {
      return null;
    }

    return (
      <>
        {fileData?.mass_shape_link && (
          <TabPane tabId={getTabId("mass_shape")}>
            {isCSV(fileData?.mass_shape_name) ? (
              isCSVContent(fileData?.massAndShapeObject) ? (
                <CsvToTable csvData={fileData?.massAndShapeObject} />
              ) : (
                <div>{t("file_error")}</div>
              )
            ) : (
              showJsonContent(fileData?.massAndShapeObject)
            )}
          </TabPane>
        )}
        {fileData?.wind_model_link && (
          <TabPane tabId={getTabId("wind_model")} className="m-3">
            {isCSV(fileData?.wind_model_name) &&
              (isCSVContent(fileData?.windModelContent) ? (
                <CsvToTable csvData={fileData?.windModelContent} />
              ) : (
                <div>{t("file_error")}</div>
              ))}
          </TabPane>
        )}
        {fileData?.target_attitude_rate_ascent_link && (
          <TabPane tabId={getTabId("target_attitude_rate_ascent")} className="m-3">
            {isCSV(fileData?.target_attitude_rate_ascent_name) &&
              (isCSVContent(fileData?.targetAttitudeRateAscentContent) ? (
                <CsvToTable csvData={fileData?.targetAttitudeRateAscentContent} />
              ) : (
                <div>{t("file_error")}</div>
              ))}
          </TabPane>
        )}
        {fileData?.target_attitude_rate_descent_link && (
          <TabPane tabId={getTabId("target_attitude_rate_descent")} className="m-3">
            {isCSV(fileData?.target_attitude_rate_descent_name) &&
              (isCSVContent(fileData?.targetAttitudeRateDescentContent) ? (
                <CsvToTable csvData={fileData?.targetAttitudeRateDescentContent} />
              ) : (
                <div>{t("file_error")}</div>
              ))}
          </TabPane>
        )}
        {fileData?.target_attitude_ascent_link && (
          <TabPane tabId={getTabId("target_attitude_ascent")} className="m-3">
            {isCSV(fileData?.target_attitude_ascent_name) &&
              (isCSVContent(fileData?.targetAttitudeAscentContent) ? (
                <CsvToTable csvData={fileData?.targetAttitudeAscentContent} />
              ) : (
                <div>{t("file_error")}</div>
              ))}
          </TabPane>
        )}
        {fileData?.target_attitude_descent_link && (
          <TabPane tabId={getTabId("target_attitude_descent")} className="m-3">
            {isCSV(fileData?.target_attitude_descent_name) &&
              (isCSVContent(fileData?.targetAttitudeDescentContent) ? (
                <CsvToTable csvData={fileData?.targetAttitudeDescentContent} />
              ) : (
                <div>{t("file_error")}</div>
              ))}
          </TabPane>
        )}
        {isHasExcelFiles(fileData) && (
          <TabPane tabId={getTabId("excel_files")} className="m-3">
            {getExcelFilesContent(fileData, simulationType, isNewVersion)}
          </TabPane>
        )}
      </>
    );
  };

  const renderJsonCompareTab = () => {
    if (!fileData?.jsonUpdateContent) return null;
    return (
      <TabPane tabId={getTabId("json_compare")}>
        <ReactDiffViewer
          className="mb-0 m-l-90"
          oldValue={safeStringify(fileData.fileContent)}
          newValue={safeStringify(fileData.jsonUpdateContent)}
          splitView
          showDiffOnly
        />
      </TabPane>
    );
  };

  return (
    <TabContent activeTab={activeTab} id="tabContentModal" className="tab-content-flight-simulator">
      {fileData && (
        <>
          {renderMainFileTab()}
          {renderSeriesResponseTab()}
          {render3DOFContent()}
          {render6DOFContent()}
          {renderJsonCompareTab()}
        </>
      )}
    </TabContent>
  );
};

export default TabContentArea;
